import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AppContext from '../../../Services/AppContext';
import PersistantData from '../../../Services/PersistantData';

const FIELD_LABEL_KEYS = {
	cust_code: 'outlet_code',
	cust_company_name: 'outlet_name',
	billing_address1: 'address',
	billing_address2: 'address_2',
	billing_state: 'state',
	billing_city: 'city',
	billing_zipcode: 'zipcode',
	billing_country: 'country',
	longitude: 'longitude',
	latitude: 'latitude',
	region: 'region',
	cust_category: 'category',
	cust_chain: 'chain',
	cust_type: 'type',
	cust_area: 'area',
	customer_status: 'status',
	created_by: 'created_by',
	created_date: 'created_date',
	updated_by: 'updated_by',
	company: 'company',
	assigned_user: 'assigned_user'
};

const ACTION_ACCENT_MAP = {
	created: 'accentCreated',
	updated: 'accentUpdated',
	active: 'accentActive',
	inactive: 'accentInactive',
	assigned: 'accentAssigned',
	unassigned: 'accentUnassigned'
};

const SOURCE_LABEL_KEYS = {
	outlet_detail: 'audit_src_outlet_detail',
	outlet_create: 'audit_src_outlet_create',
	outlet_edit: 'audit_src_outlet_edit',
	user_outlet: 'audit_src_user_outlet',
	resign: 'audit_src_resign',
	copy: 'audit_src_copy',
	checkin_approve: 'audit_src_checkin_approve'
};

const useStyles = makeStyles((theme) => ({
	detailCell: {
		padding: 0,
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.action.hover
	},
	panel: {
		margin: theme.spacing(0, 2, 1.5),
		padding: theme.spacing(2),
		borderRadius: 8,
		borderLeft: `3px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.action.hover,
		animation: '$revealPanel 220ms cubic-bezier(0.4, 0, 0.2, 1) both'
	},
	'@keyframes revealPanel': {
		from: {
			opacity: 0,
			transform: 'translateY(-4px)'
		},
		to: {
			opacity: 1,
			transform: 'none'
		}
	},
	accentCreated: {
		borderLeftColor: '#33c17a'
	},
	accentUpdated: {
		borderLeftColor: '#4d9cf0'
	},
	accentActive: {
		borderLeftColor: '#33c17a'
	},
	accentInactive: {
		borderLeftColor: '#f0554d'
	},
	accentAssigned: {
		borderLeftColor: '#26a69a'
	},
	accentUnassigned: {
		borderLeftColor: '#ffa726'
	},
	sectionTitle: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: 0.4,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1)
	},
	diffTable: {
		marginBottom: theme.spacing(2)
	},
	diffRow: {
		transition: 'background-color 150ms ease',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	diffCell: {
		paddingTop: theme.spacing(0.75),
		paddingBottom: theme.spacing(0.75),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	fieldCell: {
		fontSize: 12,
		fontWeight: 600,
		whiteSpace: 'nowrap'
	},
	arrowCell: {
		width: 28,
		paddingLeft: 0,
		paddingRight: 0,
		textAlign: 'center'
	},
	arrowIcon: {
		fontSize: 16,
		color: theme.palette.text.disabled,
		display: 'block',
		margin: '0 auto'
	},
	before: {
		fontSize: 12,
		color: '#f0554d',
		textDecoration: 'line-through'
	},
	after: {
		fontSize: 12,
		color: '#33c17a',
		fontWeight: 600
	},
	metaStrip: {
		display: 'flex',
		flexWrap: 'wrap',
		borderTop: `1px solid ${theme.palette.divider}`,
		paddingTop: theme.spacing(1.5)
	},
	metaItem: {
		marginRight: theme.spacing(4),
		marginBottom: theme.spacing(0.5)
	},
	metaKey: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(0.5)
	},
	metaValue: {
		fontSize: 12
	},
	metaMono: {
		fontSize: 12,
		fontFamily: 'Menlo, Consolas, monospace'
	},
	legacy: {
		display: 'flex',
		alignItems: 'center',
		marginTop: theme.spacing(1.5),
		padding: theme.spacing(1, 1.5),
		borderRadius: 6,
		backgroundColor: 'rgba(255, 167, 38, 0.12)'
	},
	legacyIcon: {
		fontSize: 16,
		marginRight: theme.spacing(1),
		color: '#ffa726'
	},
	legacyText: {
		fontSize: 11,
		color: theme.palette.text.secondary
	},
	'@media (prefers-reduced-motion: reduce)': {
		panel: {
			animation: 'none'
		},
		diffRow: {
			transition: 'none'
		}
	}
}));

export default function OutletAuditDetail({ row, colSpan }) {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const singleton = PersistantData.getInstance();
	var { translation: language } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const fieldLabel = (field) => {
		const key = FIELD_LABEL_KEYS[field];
		return key && language[key] ? language[key] : field;
	};

	const displayValue = (field, value) => {
		if (field === 'customer_status') {
			return parseInt(value, 10) === 1 ? language.active : language.inactive;
		}
		if (value === '' || value === 'null') {
			return '-';
		}
		return value;
	};

	const sourceLabel = (source) => {
		const key = SOURCE_LABEL_KEYS[source];
		return key && language[key] ? language[key] : source;
	};

	const changes = row.changes || [];
	const isLegacy = !row.author_name && !row.ip_address;
	const accentClass = ACTION_ACCENT_MAP[row.remark];

	return (
		<TableRow>
			<TableCell colSpan={colSpan} className={classes.detailCell}>
				<div className={`${classes.panel} ${accentClass ? classes[accentClass] : ''}`}>
					<Typography className={classes.sectionTitle}>
						{changes.length > 0 ? language.field_changes : language.audit_no_field_changes}
					</Typography>
					{
						changes.length > 0 &&
						<Table size={'small'} className={classes.diffTable}>
							<TableHead>
								<TableRow>
									<TableCell />
									<TableCell>{language.value_before}</TableCell>
									<TableCell className={classes.arrowCell} />
									<TableCell>{language.value_after}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{
									changes.map((change, index) => (
										<TableRow key={`${row.id}-${change.field}-${index}`} className={classes.diffRow}>
											<TableCell className={`${classes.diffCell} ${classes.fieldCell}`}>
												{fieldLabel(change.field)}
											</TableCell>
											<TableCell className={`${classes.diffCell} ${classes.before}`}>
												{displayValue(change.field, change.old)}
											</TableCell>
											<TableCell className={`${classes.diffCell} ${classes.arrowCell}`}>
												<ArrowRightAltIcon className={classes.arrowIcon} />
											</TableCell>
											<TableCell className={`${classes.diffCell} ${classes.after}`}>
												{displayValue(change.field, change.new)}
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					}
					<div className={classes.metaStrip}>
						<div className={classes.metaItem}>
							<Typography variant={'caption'} className={classes.metaKey} component={'span'}>
								{language.record_id}
							</Typography>
							<Typography variant={'caption'} className={classes.metaMono} component={'span'}>
								{row.cust_code || '-'}
							</Typography>
						</div>
						{
							row.source &&
							<div className={classes.metaItem}>
								<Typography variant={'caption'} className={classes.metaKey} component={'span'}>
									{language.audit_source}
								</Typography>
								<Typography variant={'caption'} className={classes.metaValue} component={'span'}>
									{sourceLabel(row.source)}
								</Typography>
							</div>
						}
						<div className={classes.metaItem}>
							<Typography variant={'caption'} className={classes.metaKey} component={'span'}>
								{language.reason_note}
							</Typography>
							<Typography variant={'caption'} className={classes.metaValue} component={'span'}>
								{row.reason || '-'}
							</Typography>
						</div>
						<div className={classes.metaItem}>
							<Typography variant={'caption'} className={classes.metaKey} component={'span'}>
								{language.ip_address}
							</Typography>
							<Typography variant={'caption'} className={classes.metaMono} component={'span'}>
								{row.ip_address || '-'}
							</Typography>
						</div>
					</div>
					{
						isLegacy &&
						<div className={classes.legacy}>
							<InfoOutlinedIcon className={classes.legacyIcon} />
							<Typography variant={'caption'} className={classes.legacyText}>
								{language.audit_legacy_record}
							</Typography>
						</div>
					}
				</div>
			</TableCell>
		</TableRow>
	);
}

export { FIELD_LABEL_KEYS };
